<template>
  <v-row
    dense
    align="center"
    justify="center"
    class="flex-nowrap"
  >
    <v-autocomplete
      v-model="dataModelItem"
      :items="itemList"
      :filter="nonDiacriticsSensitiveFilter"
      :item-text="labelFunction"
      :item-value="itemValueField"
      :label="label"
      @focus="getItemsList"
      :loading="items.isLoading"
      :no-data-text="items.isLoading ? 'loading...' : 'No data available'"
      :menu-props="{ closeOnContentClick: true }"
      :dense="dense"
      :multiple="multiple"
      :clearable="clearable"
      :chips="chips"
      :small-chips="smallChips"
      :deletable-chips="deletableChips"
      :hide-selected="hideSelected"
      :return-object="returnObject"
      @change="change"
    >
      <template v-for="(_, name) in $scopedSlots" :slot="name" slot-scope="slotData">
        <slot :name="name" v-bind="slotData"/>
      </template>
    </v-autocomplete>
    <v-tooltip
      bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          size="small"
          @click="forceUpdateList(typeObject)"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon small color="grey" >
            mdi-refresh
          </v-icon>
        </v-btn>
      </template>
      manual list refresh
    </v-tooltip>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'DataList',
  props: {
    itemModel: {
      type: [Number, Array, Object],
      default: null,
    },
    label: {
      type: String,
      default: 'data',
    },
    typeObject: {
      type: Object,
      default: null,
    },
    staticList: {
      type: Array,
      default: () => [],
    },
    itemTextFunction: {
      type: Function,
      default: (el) => el.name,
    },
    itemValueField: {
      type: String,
      default: 'id',
    },
    isUpdateOnStart: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    chips: {
      type: Boolean,
      default: false,
    },
    smallChips: {
      type: Boolean,
      default: false,
    },
    deletableChips: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      default: false,
    },
    withOptionAny: {
      type: Boolean,
      default: false,
    },
    withOptionNone: {
      type: Boolean,
      default: false,
    },
    withOptionSelectAll: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    items: {},
  }),

  created() {
    this.items = this.listItem(this.typeObject.type);
    if (this.isUpdateOnStart) {
      this.updateList(this.typeObject);
    }
  },

  methods: {
    ...mapActions({
      updateList: 'datalists/updateList',
      forceUpdateList: 'datalists/forceUpdateList',
      setStaticList: 'datalists/setStaticList',
    }),

    getItemsList() {
      if (this.typeObject.path === undefined) {
        this.setStaticList({ type: this.typeObject.type, list: this.staticList });
      } else {
        this.updateList(this.typeObject);
      }
    },

    change(obj) {
      this.$emit('change', obj);
    },

    valueWithSwitchedAnyAndNoneOptions(value) {
      if (Array.isArray(value)) {
        // set select all
        if (value.find((el) => el === this.$constants.FILTER_VALUES.SELECT_ALL
          || el[this.itemValueField] === this.$constants.FILTER_VALUES.SELECT_ALL)) {
          let filteredSelectAllValue = this.itemList.filter(
            (el) => ![
              this.$constants.FILTER_VALUES.NONE,
              this.$constants.FILTER_VALUES.ANY,
              this.$constants.FILTER_VALUES.SELECT_ALL,
            ].includes(el) && ![
              this.$constants.FILTER_VALUES.NONE,
              this.$constants.FILTER_VALUES.ANY,
              this.$constants.FILTER_VALUES.SELECT_ALL,
            ].includes(el[this.itemValueField]),
          );

          if (!this.returnObject) {
            filteredSelectAllValue = filteredSelectAllValue.map((el) => el[this.itemValueField]);
          }

          return filteredSelectAllValue;
        }

        const filteredValue = value.filter(
          (el) => [
            this.$constants.FILTER_VALUES.NONE,
            this.$constants.FILTER_VALUES.ANY,
          ].includes(el) || [
            this.$constants.FILTER_VALUES.NONE,
            this.$constants.FILTER_VALUES.ANY,
          ].includes(el[this.itemValueField]),
        );

        if (filteredValue.length === 0) {
          return value;
        }

        return value.slice(-1);
      }

      return value;
    },
  },

  computed: {
    ...mapGetters({
      listItem: 'datalists/listItem',
    }),

    dataModelItem: {
      get() {
        return this.itemModel;
      },
      set(val) {
        this.$emit('update:itemModel', this.valueWithSwitchedAnyAndNoneOptions(val));
      },
    },

    labelFunction() {
      return this.itemTextFunction;
    },

    itemList() {
      const itemsList = [...this.items.list];

      if (this.withOptionNone) {
        itemsList.unshift({ id: this.$constants.FILTER_VALUES.NONE, name: '<None>' });
      }

      if (this.withOptionAny) {
        itemsList.unshift({ id: this.$constants.FILTER_VALUES.ANY, name: '<Any>' });
      }

      if (this.withOptionSelectAll) {
        itemsList.unshift({ id: this.$constants.FILTER_VALUES.SELECT_ALL, name: '<Select All>' });
      }

      return itemsList;
    },
  },

  watch: {
    itemModel(newValue) {
      if (
        (Array.isArray(newValue) && newValue.length > 0)
        || (!Array.isArray(newValue) && newValue !== null)
      ) {
        this.updateList(this.typeObject);
      }
    },
  },
};
</script>
